import { useEffect, useState } from 'react';
import { Intro } from './components/Intro.js';
import { Inspector } from './components/Inspector.js';
import { MeaningJourney } from './components/MeaningJourney.js';
import { Settings } from './components/Settings.js';
import { DebugPanel } from './components/DebugPanel.js';
import { WorldCanvas } from './game/WorldCanvas.js';
import { sound } from './lib/sound.js';
import { useGameStore } from './store.js';

export default function App(): React.JSX.Element {
  const game = useGameStore();
  const [settings, setSettings] = useState(false);
  const selected = game.snapshot?.meanings.find((meaning) => meaning.id === game.selectedId && meaning.status !== 'popped') ?? null;

  useEffect(() => { void game.bootstrap(); }, []);
  useEffect(() => {
    if (!game.snapshot) return;
    sound.setEnabled(game.snapshot.preferences.sound);
    sound.setAmbient(game.snapshot.preferences.ambient);
  }, [game.snapshot?.preferences.sound, game.snapshot?.preferences.ambient]);

  if (game.phase === 'booting') return <Loading />;
  if (game.phase === 'error' && !game.snapshot) return <Fatal error={game.error ?? 'Не удалось войти'} />;
  if (game.phase === 'intro') return (
    <div className="app-shell app-shell--intro">
      <DistantWorld />
      <Intro returning={game.returning} busy={game.busy} onEnter={game.enterWorld} />
      {game.error && <ErrorToast message={game.error} onClose={game.clearError} />}
    </div>
  );
  if (!game.snapshot) return <Loading />;

  const activeMeanings = game.snapshot.meanings.filter((meaning) => meaning.status !== 'popped' && meaning.status !== 'archived');
  return (
    <main className="app-shell app-shell--world">
      <WorldCanvas
        snapshot={game.snapshot}
        actors={game.actors}
        ghostTracks={game.ghostTracks}
        onMeaningTap={game.selectMeaning}
        onMove={(x, y) => game.socket?.emit('player:move', { x, y })}
      />
      <header className="world-header">
        <div><span className="brand-mark" /> <strong>Смыслы</strong><small>{game.actors.length + 1} путешественников рядом</small></div>
        <button className="icon-button" onClick={() => setSettings(true)} aria-label="Настройки">☼</button>
      </header>

      <nav className="meaning-access" aria-label="Ваши смыслы">
        {activeMeanings.map((meaning) => <button key={meaning.id} onClick={() => game.selectMeaning(meaning.id)}>{meaning.title}<span>{meaning.importance}</span></button>)}
      </nav>

      {game.snapshot.releasedEnergy > 0 && (
        <div className="energy-tray">
          <span className="energy-tray__motes" />
          <div><strong>Освобождено {game.snapshot.releasedEnergy} единиц жизни</strong><small>Коснитесь шара, чтобы вернуть энергию</small></div>
        </div>
      )}

      {!selected && <MeaningJourney snapshot={game.snapshot} busy={game.busy} onChoose={game.chooseMeanings} />}
      {selected && (
        <Inspector
          meaning={selected}
          meanings={activeMeanings}
          releasedEnergy={game.snapshot.releasedEnergy}
          haptics={game.snapshot.preferences.haptics}
          onClose={() => game.selectMeaning(null)}
          onUpdate={(patch) => game.updateMeaning(selected.id, patch)}
          onPop={() => game.popMeaning(selected.id)}
          onRemoveShell={() => game.removeShell(selected.id)}
          onSplit={(titles) => game.splitMeaning(selected.id, titles)}
          onMerge={(targetId) => game.mergeMeanings(selected.id, targetId)}
          onTransfer={() => game.transferEnergy(selected.id)}
        />
      )}
      {settings && <Settings preferences={game.snapshot.preferences} onChange={game.updatePreferences} onClose={() => setSettings(false)} />}
      {game.error && <ErrorToast message={game.error} onClose={game.clearError} />}
      {import.meta.env.DEV && <DebugPanel onGhost={() => game.debugActor('ghost')} onLive={() => game.debugActor('live')} onMeaning={() => void game.sendMessage('Мне нравится создавать новые проекты')} onShell={() => void game.sendMessage('Я хочу доказать всем свою успешность')} onReconnect={game.simulateReconnect} />}
    </main>
  );
}

function Loading(): React.JSX.Element {
  return <main className="loading"><span className="loading__orb" /><p>Мир проявляется…</p></main>;
}

function Fatal({ error }: { error: string }): React.JSX.Element {
  return <main className="fatal"><span className="brand-mark" /><h1>Мир пока не открылся</h1><p>{error}</p><div className="fatal__actions"><a className="button button--primary" href="https://t.me/smysly_psihomag_bot?startapp">Открыть в Telegram</a><button className="button button--ghost" onClick={() => window.location.reload()}>Попробовать снова</button></div></main>;
}

function DistantWorld(): React.JSX.Element {
  return <div className="distant-world" aria-hidden="true">{Array.from({ length: 8 }, (_, index) => <i key={index} style={{ '--i': index } as React.CSSProperties}><b /><span /></i>)}</div>;
}

function ErrorToast({ message, onClose }: { message: string; onClose(): void }): React.JSX.Element {
  return <div className="error-toast" role="alert"><span>{message}</span><button onClick={onClose} aria-label="Закрыть">×</button></div>;
}
